import React, { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import classNames from 'classnames';

import { deriveMapStateFromConfig, EWKT, TWC } from '@tmapy/beetlejuice-utils';
import { useMessage } from '@tmapy/intl';
import { EWKTToGeoJSONObject } from '@tmapy/mapcore';
import { useDispatch } from '@tmapy/redux';

import './SvuDetail.scss';

import {
  DangerAlert,
  WaitingAlert,
  FormItem,
  BtnBar,
  CtaBtn,
  SuccessAlert,
  Input,
  Form,
  SecondaryBtn,
  CustomSelect,
  UploadDropZone,
  TertiaryBtn,
  SvgCross,
  InfoAlert,
  Table,
} from '@tmapy/style-guide';

import { svuBtnMessages, svuMessages, svuQueryMessages, svuValidationErrors } from './svuMessages';

import {
  SSE_TYPE,
  useAppContext,
  useAuth,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useSelect,
  useSelector,
  useSSE,
  useAction,
  toBtnQueryState,
} from '../../hooks';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { mapConfig } from '../mapConfig';
import { MapWrapper } from '../../app/components/MapWrapper';
import { MapWithAreas } from '../../areas/components/MapWithAreas';
import { QUERY_STATE } from '../../constants';
import { commonMessages } from '../../commonMessages';

import type { NestedSubjektResponse } from '../../openapi/types';
import { r7UrceniEditoraZpsPartialUpdate } from '../../openapi/operations/r7UrceniEditoraZpsPartialUpdate';
import { r7UrceniEditoraZpsDestroy } from '../../openapi/operations/r7UrceniEditoraZpsDestroy';
import {
  R7UrceniEditoraZpsRetrieveResponse,
  useR7UrceniEditoraZpsRetrieveResource,
} from '../../openapi/operations/r7UrceniEditoraZpsRetrieve';
import { r7UrceniEditoraZpsCreate } from '../../openapi/operations/r7UrceniEditoraZpsCreate';
import { docsListOperation } from '../../openapi/operations/docsList';
import { subjektyList } from '../../openapi/operations/subjektyList';
import { useMonitoringTimeline } from '../../monitoring/hooks/useMonitoringTimeline';
import { monitoringMessages } from '../../monitoring/messages';
import { StateBadge } from './StateBadge';

type DetailProps = {
  svuId?: string;
};

export const SvuDetail: React.FC<DetailProps> = React.memo((props) => {
  const svuId = parseInt(props.svuId ?? '', 10);

  const monitoringSubject = useMemo(() => (svuId ? { urceny_editor_zps: svuId } : null), [svuId]);
  const timeline = useMonitoringTimeline(monitoringSubject);

  useSSE(SSE_TYPE.SVU, svuId, timeline.markStale);
  const { appType } = useAppContext();
  const auth = useAuth();
  const dispatch = useDispatch();
  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [deleteQueryState, setDeleteQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);
  const [validate, setValidate] = useState(false);
  const mapCore = useSelector((state) => state.mapCore);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const resource = useR7UrceniEditoraZpsRetrieveResource(svuId ? { id: svuId } : null);
  const data = useMemo(() => {
    return (resource.data ? { ...resource.data } : {}) as R7UrceniEditoraZpsRetrieveResponse;
  }, [resource.data]);

  const mapState = useMemo(() => deriveMapStateFromConfig({ map: mapConfig() } as TWC), []);
  const feature = useMemo(() => {
    if (!data.uzemni_pusobnost || uploadFile) {
      return [];
    }
    // Convert the EWKT geometry to GeoJSON using the target projection from mapCore
    const targetProjection = mapCore.view.projection;
    const feature = EWKTToGeoJSONObject(data.uzemni_pusobnost as EWKT, targetProjection);
    return [feature];
  }, [data.uzemni_pusobnost, mapCore.view.projection, uploadFile]);

  const formatMessage = useMessage();

  // ACL
  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('R7aUrceniEditoraZps', 'partial_update', data),
      svuUpdate: auth.hasPermission('R7aUrceniEditoraZps', 'update', data),
      svuDestroy: auth.hasPermission('R7aUrceniEditoraZps', 'destroy', data),
    }),
    [auth, data],
  );

  const routeList = useHistoryAction('managerSvuList', { appType });

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);
  }, [setSaveQueryState]);

  // NAZEV
  const nazev = useInput({
    name: 'nazev',
    isDisabled: !acl.canUpdate || !!svuId,
    initialValue: data.nazev,
    validate: validate,
    validator: (value) => (!value ? formatMessage(svuValidationErrors.nameError) : null),
    isRequired: true,
    autoFocus: !svuId,
    label: formatMessage(svuMessages.name),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.nazev,
  });

  // ID UZEMNI EDITACE
  const id_uzemni_editace = useInput({
    name: 'id_uzemni_editace',
    isDisabled: true,
    initialValue: data.id_uzemni_editace,
    label: formatMessage(svuMessages.idUzemniEditace),
  });

  // VYJADRENI EDITOR
  // SUBJEKT
  const renderSubjektValue = useCallback((item?: { jmeno?: string; id_subjektu?: string }) => {
    const jmeno = item?.jmeno || '';
    const idSubjektu = item?.id_subjektu ? ` (${item.id_subjektu})` : '';
    return `${jmeno}${idSubjektu}`;
  }, []);
  const renderSubjektSelectItem = (item: NestedSubjektResponse) => <>{renderSubjektValue(item)}</>;
  const prepareSubjektyItems = useCallback(async () => {
    const response = await subjektyList({ query: { registry: ['EDITOR_ZPS'], page_size: 9999 } });
    if (response.ok) {
      return response.data?.results ?? null;
    }
    return null;
  }, []);
  const subjektChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);
  }, [setValidate, setSaveQueryState]);

  const editor = useSelect<NestedSubjektResponse | undefined, NestedSubjektResponse>({
    name: 'editor',
    isDisabled: !acl.canUpdate || !!svuId,
    label: formatMessage(svuMessages.vyjadreniEditor),
    initialValue: data.editor,
    validate: validate,
    validator: (value) =>
      !value?.id ? formatMessage(svuValidationErrors.vyjadreniEditorError) : null,
    onChange: subjektChangeCallback,
    prepareItems: prepareSubjektyItems,
    renderValue: renderSubjektValue,
    renderItem: renderSubjektSelectItem,
    ...formItemServerErrors.editor,
  });

  // UPLOAD FILE VALIDATION
  const uploadFileValidation = useInput({
    name: 'uploadFileValidation',
    isDisabled: !acl.canUpdate,
    isRequired: svuId ? false : true,
    validate: validate,
    validator: () =>
      !svuId && !uploadFile ? formatMessage(svuValidationErrors.fileUploadError) : null,
    label: formatMessage(svuMessages.fileUpload),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.uzemni_pusobnost_soubor,
  });

  // ATTACHMENT
  const uploadHelpText = formatMessage.fallback(['svu.attachments.uploadHelpText']);

  const formItems = {
    nazev,
    editor,
    uploadFileValidation,
  } as const;

  // ACTIONS
  const actionQuery = useMemo(() => ({ id: svuId }), [svuId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
    routeList,
    listPath: docsListOperation().path,
  });

  const handleDeleteBtnClick = () => {
    if (window.confirm(formatMessage(svuBtnMessages.deleteAlert))) {
      doAction(r7UrceniEditoraZpsDestroy, setDeleteQueryState, {
        refetch: false,
        redirectToList: true,
      });
    }
  };

  const handleSave = useCallback(async () => {
    setValidate(true);

    const formData = new FormData() as FormData;
    uploadFile && formData.set('uzemni_pusobnost_soubor', uploadFile);
    editor.value?.url && formData.set('editor', editor.value.url);
    formData.set('nazev', nazev.value || '');

    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      setSaveQueryState(QUERY_STATE.WAITING);

      try {
        const response = svuId
          ? await r7UrceniEditoraZpsPartialUpdate({
              query: { id: svuId },
              request: formData as any,
            })
          : await r7UrceniEditoraZpsCreate({
              request: formData as any,
            });

        if (response.ok) {
          setSaveQueryState(QUERY_STATE.SUCCESS);
          // presmeruje na seznam SVU
          routeList();
        } else {
          setSaveQueryState(QUERY_STATE.FAIL);
        }
      } catch (e) {
        setSaveQueryState(QUERY_STATE.FAIL);
      }
    }
    return null;
  }, [formItems]);

  // UPLOAD HANDLER
  const handleUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      setUploadFile(input.files ? input.files[0] : null);
      // If the input is empty, we reset the file state
      input.value = '';
    },
    [dispatch],
  );

  const handleDeleteFileBtnClick = useCallback(
    (_: React.MouseEvent, eventData: any) => {
      if (window.confirm(formatMessage(svuBtnMessages.deleteFileAlert))) {
        setUploadFile(null);
      }
    },
    [setUploadFile],
  );

  return (
    <MainContent width='full'>
      <article className='sg-u-vs-4'>
        <div className='dmx-doc--head sg-u-vs-2'>
          {(resource.queryState === QUERY_STATE.SUCCESS || !svuId) && (
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-b sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-u-hsl-1'>
                <PageTitle>
                  <FormattedMessage {...svuMessages.title} values={{ id: data.id || 'empty' }} />
                </PageTitle>
                {data.state && <StateBadge state={data.state} />}
              </header>
              <BtnBar>
                {acl.svuUpdate && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSave}
                  >
                    <FormattedMessage {...svuBtnMessages.save} />
                  </CtaBtn>
                )}
                {!!svuId && acl.svuDestroy && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(deleteQueryState)}
                    isDisabled={isActionRunning}
                    isDanger
                    onClick={handleDeleteBtnClick}
                  >
                    <FormattedMessage {...svuBtnMessages.delete} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
          )}

          {(saveQueryState === QUERY_STATE.WAITING ||
            resource.queryState === QUERY_STATE.WAITING) && (
            <WaitingAlert>
              <FormattedMessage {...svuQueryMessages.submitQueryStateWaiting} />
            </WaitingAlert>
          )}

          {resource.queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...svuQueryMessages.loadQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...svuQueryMessages.submitQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.SUCCESS && (
            <SuccessAlert>
              <FormattedMessage {...svuQueryMessages.submitQueryStateSuccess} />
            </SuccessAlert>
          )}

          {deleteQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...svuQueryMessages.deleteQueryStateFail} />
            </DangerAlert>
          )}
        </div>
        {(resource.queryState === QUERY_STATE.SUCCESS || !svuId) && (
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <Form onSubmit={handleSave}>
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                  <div className='sg-u-box sg-a-p-2 sg-a-d-f sg-a-fw-w sg-a-g-2'>
                    <div className='sg-a-f-a sg-u-vs-1'>
                      <FormItem {...nazev}>
                        <Input {...nazev} />
                      </FormItem>
                      {svuId ? (
                        <FormItem {...id_uzemni_editace}>
                          <Input {...id_uzemni_editace} />
                        </FormItem>
                      ) : null}
                      <FormItem {...editor} isRequired>
                        <CustomSelect {...editor} />
                      </FormItem>
                      <FormItem {...uploadFileValidation}>
                        {uploadHelpText && (
                          <div className='sg-a-pb-1'>
                            <InfoAlert>{uploadHelpText}</InfoAlert>
                          </div>
                        )}
                        <div className='sg-a-d-f'>
                          {uploadFile && (
                            <ul className='svu-detail-uploaded-file'>
                              <li
                                key={uploadFile.name}
                                className='sg-a-d-f sg-a-ai-b'
                                data-icon-btn-hover
                              >
                                <span>{uploadFile.name}</span>
                                <TertiaryBtn
                                  icon={{ element: <SvgCross /> }}
                                  isDanger
                                  isSuppressed
                                  tooltip={formatMessage(svuMessages.deleteUploadFileBtn)}
                                  onClick={handleDeleteFileBtnClick}
                                />
                              </li>
                            </ul>
                          )}

                          <div className='dmx-newOrderShpDgnAttachments-dropZone'>
                            <UploadDropZone
                              label={formatMessage(commonMessages.uploadDropZone)}
                              btnLabel={formatMessage(commonMessages.uploadBtn)}
                              isDisabled={(uploadFile ? true : false) || !acl.canUpdate}
                              name='attachments'
                              isShown={true}
                              onDrop={handleUpload}
                            />
                          </div>
                        </div>
                      </FormItem>
                    </div>
                  </div>
                </section>
              </Form>
              {mapState && feature && svuId ? (
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...svuMessages.uzemniPusobnost} />
                  </h3>
                  <div
                    className={classNames({
                      'sg-u-box': true,
                      'sg-a-p-2': true,
                      'sg-a-d-f': true,
                      'sg-a-fw-w': true,
                      'sg-a-g-2': true,
                      'svu-detail-map-container': true,
                      'svu-detail--is-disabled': uploadFile,
                    })}
                  >
                    <div className='sg-a-f-a sg-u-vs-1'>
                      <MapWrapper>
                        <MapWithAreas state={mapState} features={feature} />
                      </MapWrapper>
                    </div>
                  </div>
                </section>
              ) : null}
            </div>
            {svuId ? (
              <div className='sg-u-vs-4'>
                <section>
                  <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                    <FormattedMessage {...svuMessages.sectionKraje} />
                  </h3>
                  <div className='sg-u-box sg-a-p-2 sg-a-d-f sg-a-fw-w sg-a-g-2'>
                    <div className='sg-a-f-a sg-u-vs-1'>
                      <Table>
                        <tbody>
                          {timeline.rows.map((row) => (
                            <tr key={row.id}>
                              <td
                                className={classNames({
                                  'sg-a-w-1px': true,
                                  'dmx-u-stateColumn': true,
                                  [`svu-logList--level-${row.level?.toLowerCase()}`]: !!row.level,
                                })}
                                title={row.level ?? ''}
                              />
                              <td className='sg-a-va-b sg-a-ta-r sg-a-ws-nw sg-a-fvn-tn'>
                                <time
                                  className='sg-u-type-sml sg-u-c-body-darkest'
                                  dateTime={row.created}
                                >
                                  <FormattedDate value={new Date(row.created)} />{' '}
                                  <FormattedTime
                                    value={new Date(row.created)}
                                    hour='numeric'
                                    minute='numeric'
                                    second='numeric'
                                  />
                                </time>
                              </td>
                              <td className='sg-a-va-b'>
                                <pre className='sg-u-type-xsm sg-a-ws-pw'>{row.message}</pre>
                              </td>
                              <td className='sg-a-va-b'>
                                <pre className='sg-u-type-xsm sg-a-ws-pw'>
                                  {row.operation_label || row.operation}
                                </pre>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      {timeline.hasMore && (
                        <BtnBar>
                          <SecondaryBtn
                            size='sml'
                            queryState={timeline.isLoadingMore ? 'waiting' : undefined}
                            onClick={timeline.loadMore}
                          >
                            <FormattedMessage
                              {...monitoringMessages.timelineLoadMore}
                              values={{ count: timeline.nextPageCount }}
                            />
                          </SecondaryBtn>
                          <span className='sg-u-type-sml sg-u-c-body-light'>
                            <FormattedMessage
                              {...monitoringMessages.timelineShown}
                              values={{ shown: timeline.rows.length, count: timeline.count }}
                            />
                          </span>
                        </BtnBar>
                      )}
                    </div>
                  </div>
                </section>
              </div>
            ) : null}
          </div>
        )}
      </article>
    </MainContent>
  );
});
